import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Scale, X, Check, Calculator, AlertTriangle, ArrowRight } from 'lucide-react';

export interface UomConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  itemUnit: string;
  currentReceivedQty: number;
  approvedQty: number;
  onApplyConversion: (calculatedPieces: number, proofNote: string) => void;
}

export const UomConversionModal: React.FC<UomConversionModalProps> = ({
  isOpen,
  onClose,
  itemName,
  itemUnit = 'und',
  currentReceivedQty,
  approvedQty,
  onApplyConversion
}) => {
  const [guiaQty, setGuiaQty] = useState<string>('');
  const [guiaUnit, setGuiaUnit] = useState<string>('kg');
  const [factorUnitario, setFactorUnitario] = useState<string>('');
  const [roundMode, setRoundMode] = useState<'round' | 'floor' | 'ceil'>('round');

  useEffect(() => {
    if (!isOpen) return;
    setGuiaQty('');
    // Auto-detect unit hint based on item name
    const lowerName = itemName.toLowerCase();
    if (lowerName.includes('cable') || lowerName.includes('manguera') || lowerName.includes('cinta')) {
      setGuiaUnit('m');
    } else {
      setGuiaUnit('kg');
    }

    // Heuristics for typical theoretical weights/lengths if known
    if (lowerName.includes('100x100') || lowerName.includes('100 x 100')) {
      setFactorUnitario('24.8'); // ~24.8 kg por tubo 6m
    } else if (lowerName.includes('80x40') || lowerName.includes('80 x 40')) {
      setFactorUnitario('14.2');
    } else if (lowerName.includes('lámina') || lowerName.includes('lamina')) {
      setFactorUnitario('37.5'); // ~37.5 kg por lámina galvanizada
    } else {
      setFactorUnitario('');
    }
    setRoundMode('round');
  }, [isOpen, itemName]);

  if (!isOpen) return null;

  const numGuia = parseFloat(guiaQty) || 0;
  const numFactor = parseFloat(factorUnitario) || 0;

  let rawCalculated = 0;
  let finalPieces = 0;

  if (numGuia > 0 && numFactor > 0) {
    rawCalculated = numGuia / numFactor;
    if (roundMode === 'floor') finalPieces = Math.floor(rawCalculated);
    else if (roundMode === 'ceil') finalPieces = Math.ceil(rawCalculated);
    else finalPieces = Math.round(rawCalculated);
  }

  const diffFromApproved = approvedQty > 0 ? finalPieces - approvedQty : 0;
  const proofNote = numGuia > 0 && numFactor > 0
    ? `[Guía: ${numGuia.toLocaleString('es-VE')} ${guiaUnit} @ ${numFactor.toLocaleString('es-VE')} ${guiaUnit}/${itemUnit} = ${finalPieces} ${itemUnit}]`
    : '';

  const handleApply = () => {
    if (finalPieces <= 0) {
      alert('El cálculo debe dar al menos 1 unidad para aplicar la conversión.');
      return;
    }
    onApplyConversion(finalPieces, proofNote);
    onClose();
  };

  const content = (
    <div className="fixed inset-0 z-[10000] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
        {/* Cabecera */}
        <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg">
              <Scale className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                Asistente de Conversión de Unidades (Muelle)
              </h3>
              <p className="text-[11px] text-slate-400">
                Convierte remisiones en kg o metros a piezas enteras para inventario
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-5 space-y-4 text-xs">
          {/* Ficha del Insumo */}
          <div className="p-3 bg-slate-800/50 border border-slate-700/60 rounded-xl flex items-center justify-between">
            <div className="truncate pr-2">
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Insumo a Recibir</span>
              <span className="text-sm font-bold text-white truncate block">{itemName}</span>
            </div>
            <div className="text-right shrink-0">
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Aprobado OAB</span>
              <span className="text-xs font-mono font-bold text-emerald-400">{approvedQty} {itemUnit}</span>
            </div>
          </div>

          {/* Formulario de Conversión */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                Cantidad según Guía / Remisión:
              </label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="ej. 1240.50"
                  value={guiaQty}
                  onChange={(e) => setGuiaQty(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono text-sm focus:border-indigo-500 focus:outline-none"
                  autoFocus
                />
                <select
                  value={guiaUnit}
                  onChange={(e) => setGuiaUnit(e.target.value)}
                  className="px-2 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-300 font-mono text-xs focus:border-indigo-500 focus:outline-none"
                >
                  <option value="kg">kg</option>
                  <option value="m">m</option>
                  <option value="ton">ton</option>
                  <option value="g">g</option>
                  <option value="lb">lb</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                Factor Teórico por Unidad:
              </label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="any"
                  min="0.0001"
                  placeholder="ej. 24.8"
                  value={factorUnitario}
                  onChange={(e) => setFactorUnitario(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono text-sm focus:border-indigo-500 focus:outline-none"
                />
                <span className="px-2 py-2 bg-slate-800 border border-slate-700 rounded-lg text-[10px] text-slate-400 font-mono whitespace-nowrap">
                  {guiaUnit}/{itemUnit}
                </span>
              </div>
            </div>
          </div>

          {/* Modalidad de Redondeo */}
          <div className="flex items-center justify-between p-2.5 bg-slate-800/30 border border-slate-700/50 rounded-lg">
            <span className="text-[11px] text-slate-300 font-medium">Modo de redondeo a piezas:</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setRoundMode('round')}
                className={`px-2.5 py-1 text-[10px] font-bold rounded ${roundMode === 'round' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
              >
                Matemático
              </button>
              <button
                type="button"
                onClick={() => setRoundMode('floor')}
                className={`px-2.5 py-1 text-[10px] font-bold rounded ${roundMode === 'floor' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
              >
                Piso (Truncar)
              </button>
              <button
                type="button"
                onClick={() => setRoundMode('ceil')}
                className={`px-2.5 py-1 text-[10px] font-bold rounded ${roundMode === 'ceil' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
              >
                Techo
              </button>
            </div>
          </div>

          {/* Panel de Resultado */}
          <div className="p-4 bg-gradient-to-r from-indigo-950/40 via-slate-800 to-indigo-950/40 border border-indigo-500/30 rounded-xl text-center">
            <span className="text-[10px] uppercase tracking-wider text-indigo-300 font-bold block mb-1">
              Resultado Equivalente para Inventario:
            </span>
            <div className="flex items-center justify-center gap-3">
              <span className="text-3xl font-black font-mono text-white tracking-tight">
                {finalPieces}
              </span>
              <span className="text-sm font-bold text-indigo-300 uppercase font-mono">
                {itemUnit}
              </span>
            </div>
            {rawCalculated > 0 && (
              <span className="text-[10px] text-slate-400 font-mono block mt-1">
                (Exacto: {rawCalculated.toFixed(3)} {itemUnit})
              </span>
            )}

            {/* Comparativa con OAB */}
            {approvedQty > 0 && finalPieces > 0 && (
              <div className="mt-2.5 flex items-center justify-center gap-2">
                {diffFromApproved === 0 ? (
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded text-[10px] font-bold">
                    ✓ Coincide 100% con la cantidad aprobada
                  </span>
                ) : diffFromApproved > 0 ? (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded text-[10px] font-bold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    +{diffFromApproved} {itemUnit} sobre la OAB (+{((diffFromApproved / approvedQty) * 100).toFixed(1)}%)
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded text-[10px] font-bold">
                    {diffFromApproved} {itemUnit} (Entrega parcial / Saldo: {Math.abs(diffFromApproved)})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Nota Probatoria para Kardex */}
          {proofNote && (
            <div className="p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-[10px] text-slate-300">
              <span className="font-bold text-slate-400 block mb-0.5">Nota de Auditoría que se anexará al renglón:</span>
              <code className="text-indigo-300 font-mono">{proofNote}</code>
            </div>
          )}
        </div>

        {/* Footer de Acciones */}
        <div className="p-4 bg-slate-800/80 border-t border-slate-700 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-semibold rounded-xl text-xs transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={finalPieces <= 0}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold rounded-xl text-xs shadow-lg shadow-indigo-600/30 transition"
          >
            <Check className="w-4 h-4" />
            <span>Aplicar {finalPieces > 0 ? `${finalPieces} ${itemUnit}` : ''} a Rampa</span>
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : null;
};
